import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, ViewContainerRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { CustomActiveCartService } from '@cart/data-access';
import { ValidationErrorKeys } from '@checkout/const';
import { ApplyingBonusService, CheckoutValidationService, MIN_ALLOWED_AMOUNT } from '@checkout/feature';
import { concatLatestFrom } from '@ngrx/operators';
import { ScrollToService } from '@shared/v2';
import { BonusSystemFeatureAvailableService, CurrentProfileService } from '@user/feature';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map } from 'rxjs/operators';

@Component({
  selector: 'sd-checkout-bonus',
  templateUrl: './checkout-bonus.component.html',
  styleUrls: ['./checkout-bonus.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SdvorCheckoutBonusComponent implements OnInit {
  formControl = new FormControl<number | undefined>(undefined);
  enabled$ = new BehaviorSubject<boolean | undefined>(undefined);
  isValid$ = this.applyingBonusService.isValid$;
  useBonusLimit$ = this.bonusSystemFeatureAvailableService.useBonusLimit();

  minAllowedAmount = MIN_ALLOWED_AMOUNT;

  minAllowableBalance$: Observable<number>;
  maxAllowableBalance$: Observable<number>;
  isSwitchDisabled$: Observable<boolean>;

  balanceIsNegative$: Observable<boolean>;

  constructor(
    private activeCartService: CustomActiveCartService,
    private currentProfileService: CurrentProfileService,
    private applyingBonusService: ApplyingBonusService,
    private destroyRef: DestroyRef,
    private checkoutValidationService: CheckoutValidationService,
    private viewRef: ViewContainerRef,
    private scrollToService: ScrollToService,
    private bonusSystemFeatureAvailableService: BonusSystemFeatureAvailableService,
  ) {}

  ngOnInit(): void {
    this.applyingBonusService.validate().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    // максимум баллов, которые может применить пользователь
    this.maxAllowableBalance$ = combineLatest({
      totalSalesOnly: this.activeCartService.getActive().pipe(map((cart) => cart?.totalSalesOnly?.value ?? 0)),
      userBonusBalance: this.currentProfileService.get().pipe(map((profile) => profile.bonusBalance?.value ?? 0)),
    }).pipe(
      map(({ totalSalesOnly, userBonusBalance }) => {
        if (totalSalesOnly < userBonusBalance) {
          return Math.floor(totalSalesOnly);
        } else {
          return userBonusBalance;
        }
      }),
    );

    // мимнимум баллов, которые может применить пользователь
    this.minAllowableBalance$ = combineLatest({
      useBonusLimit: this.useBonusLimit$,
      maxAllowableBalance: this.maxAllowableBalance$,
    }).pipe(
      map(({ useBonusLimit, maxAllowableBalance }) => {
        if (maxAllowableBalance === 0) {
          return 0;
        }

        if (useBonusLimit) {
          return maxAllowableBalance < MIN_ALLOWED_AMOUNT ? 0 : MIN_ALLOWED_AMOUNT;
        }

        return 1;
      }),
    );

    this.isSwitchDisabled$ = this.minAllowableBalance$.pipe(map((minAllowableBalance) => minAllowableBalance === 0));

    this.balanceIsNegative$ = this.maxAllowableBalance$.pipe(map((balance) => balance < 0));

    // обработка ввода пользователем
    this.formControl.valueChanges
      .pipe(
        distinctUntilChanged(),
        debounceTime(500),
        concatLatestFrom(() =>
          combineLatest({
            maxAllowableBalance: this.maxAllowableBalance$,
            useBonusLimit: this.useBonusLimit$,
          }),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([appliedValue, { maxAllowableBalance, useBonusLimit }]) => {
        let value = appliedValue ?? 0;

        if (this.enabled$.getValue()) {
          const minAllowableBalance = useBonusLimit ? MIN_ALLOWED_AMOUNT : 1;

          if (!value) {
            value = minAllowableBalance <= maxAllowableBalance ? minAllowableBalance : 0;
          }

          if (value > maxAllowableBalance) {
            value = maxAllowableBalance;
          }
        }

        this.formControl.setValue(value, { emitEvent: false });
        this.applyingBonusService.setApplyingBonus(value);
      });

    // инициализация значения
    combineLatest({
      totalSalesOnly: this.activeCartService.getActive().pipe(map((cart) => cart?.totalSalesOnly?.value ?? 0)),
      applyingAmountFromCart: this.activeCartService.getActive().pipe(
        map((cart) => cart?.bonus?.applyingAmountInfo?.value ?? 0),
        takeUntilDestroyed(this.destroyRef),
      ),
      minAllowableBalance: this.minAllowableBalance$,
      maxAllowableBalance: this.maxAllowableBalance$,
    })
      .pipe(distinctUntilChanged((a, b) => a.totalSalesOnly === b.totalSalesOnly))
      .subscribe(({ applyingAmountFromCart, minAllowableBalance, maxAllowableBalance }) => {
        let value = 0;

        if (
          applyingAmountFromCart > 0 &&
          minAllowableBalance > 0 &&
          applyingAmountFromCart >= minAllowableBalance &&
          applyingAmountFromCart <= maxAllowableBalance
        ) {
          value = applyingAmountFromCart;
        }

        if (this.formControl.value !== value) {
          this.formControl.setValue(value);
          this.enabled$.next(value > 0);
        }
      });

    this.isSwitchDisabled$
      .pipe(
        filter((isDisabled) => isDisabled),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (this.enabled$.getValue()) {
          this.enabled$.next(false);
        }
      });

    // включение/выключение свитча
    this.enabled$
      .pipe(
        filter((isEnabled) => isEnabled !== undefined),
        distinctUntilChanged(),
        concatLatestFrom(() => this.maxAllowableBalance$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([isEnabled, maxAllowableBalance]) => {
        if (isEnabled && !this.formControl.value) {
          this.formControl.setValue(maxAllowableBalance);
        }

        if (!isEnabled && this.formControl.value) {
          this.formControl.setValue(0);
        }
      });

    // скролл к ошибке
    this.checkoutValidationService
      .getScrollToEvent(ValidationErrorKeys.MINIMUM_BONUS_VALUE, ValidationErrorKeys.BONUS_APPLYING_FAILED)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.scrollToService.scrollToElement(this.viewRef.element.nativeElement, { offset: -100 });
      });
  }

  enabledChanged(isEnabled: boolean): void {
    this.enabled$.next(isEnabled);
  }
}
